import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabaseClient'
import { useAuth } from '@/features/auth/AuthContext'

function ConversationList({ conversations, selectedId, onSelect, unreadCounts }) {
  if (!conversations || conversations.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-secondary-400 mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>
        <p className="text-sm text-secondary-600">No conversations yet</p>
      </div>
    )
  }

  return (
    <div className="divide-y divide-secondary-200">
      {conversations.map((conv) => {
        const unreadCount = unreadCounts[conv.id] || 0
        const isSelected = conv.id === selectedId

        return (
          <button
            key={conv.id}
            onClick={() => onSelect(conv)}
            className={`w-full text-left px-4 py-4 hover:bg-secondary-50 transition-colors ${
              isSelected ? 'bg-primary-50' : ''
            }`}
          >
            <div className="flex items-start justify-between mb-1">
              <h4 className="text-sm font-medium text-secondary-900">
                {conv.buyer?.full_name || 'Unknown Buyer'}
              </h4>
              {unreadCount > 0 && (
                <span className="bg-primary-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            <p className="text-xs text-secondary-600 mb-1">
              {conv.project?.name || 'Project'}
            </p>
            {conv.last_message_at && (
              <p className="text-xs text-secondary-500">
                {new Date(conv.last_message_at).toLocaleDateString('en-NZ', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            )}
          </button>
        )
      })}
    </div>
  )
}

function ConversationView({ conversation, onBack }) {
  const { user } = useAuth()
  const [newMessage, setNewMessage] = useState('')
  const [sendError, setSendError] = useState(null)
  const [sending, setSending] = useState(false)

  const { data: messagesData, isLoading, refetch } = useQuery({
    queryKey: ['conversation-messages', conversation.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('conversation_messages')
        .select(`
          *,
          sender:sender_id (
            id,
            full_name,
            role
          )
        `)
        .eq('conversation_id', conversation.id)
        .order('created_at', { ascending: true })

      if (error) throw error
      return data
    },
  })

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!newMessage.trim() || sending) return

    setSending(true)
    setSendError(null)

    const { error } = await supabase
      .from('conversation_messages')
      .insert({
        conversation_id: conversation.id,
        sender_id: user.id,
        content: newMessage.trim(),
      })

    if (error) {
      setSendError(error.message)
      setSending(false)
      return
    }

    await supabase
      .from('project_conversations')
      .update({ last_message_at: new Date().toISOString() })
      .eq('id', conversation.id)

    setNewMessage('')
    setSending(false)
    refetch()
  }

  const messages = messagesData || []

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 border-b border-secondary-200 bg-white">
        <button
          onClick={onBack}
          className="text-primary-600 hover:text-primary-700 mb-2 md:hidden"
        >
          ← Back
        </button>
        <h3 className="text-lg font-semibold text-secondary-900">
          {conversation.buyer?.full_name || 'Unknown Buyer'}
        </h3>
        <p className="text-sm text-secondary-600">
          {conversation.project?.name || 'Project'}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-secondary-500 text-sm">No messages yet</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.sender_id === user.id
            return (
              <div
                key={msg.id}
                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[70%] ${isOwn ? 'order-2' : 'order-1'}`}>
                  <div
                    className={`rounded-lg px-4 py-2 ${
                      isOwn
                        ? 'bg-primary-600 text-white'
                        : 'bg-secondary-100 text-secondary-900'
                    }`}
                  >
                    <p className="text-sm font-medium mb-1">
                      {msg.sender?.full_name}
                    </p>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    <p
                      className={`text-xs mt-1 ${
                        isOwn ? 'text-primary-100' : 'text-secondary-500'
                      }`}
                    >
                      {new Date(msg.created_at).toLocaleString('en-NZ', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      <form onSubmit={handleSendMessage} className="p-4 border-t border-secondary-200 bg-white">
        {sendError && (
          <div className="mb-2 p-2 bg-error-50 border border-error-200 rounded text-sm text-error-700">
            {sendError}
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="input flex-1"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="btn-primary px-6 py-2"
          >
            {sending ? 'Sending...' : 'Send'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default function MessageCenter({ developerId, projectId = null }) {
  const [selectedConversation, setSelectedConversation] = useState(null)

  const { data: conversationsData, isLoading } = useQuery({
    queryKey: ['developer-conversations', developerId, projectId],
    queryFn: async () => {
      let query = supabase
        .from('project_conversations')
        .select(`
          *,
          project:project_id (
            id,
            name,
            developer_id
          ),
          buyer:buyer_id (
            id,
            full_name,
            email
          )
        `)

      if (projectId) {
        query = query.eq('project_id', projectId)
      }

      query = query
        .order('last_message_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })

      const { data, error } = await query

      if (error) throw error

      if (projectId) {
        return data
      }

      return data.filter(conv => conv.project?.developer_id === developerId)
    },
  })

  const { data: unreadCountsData } = useQuery({
    queryKey: ['conversation-unread-counts', developerId, projectId],
    queryFn: async () => {
      const conversations = conversationsData || []
      const counts = {}

      for (const conv of conversations) {
        const { count, error } = await supabase
          .from('conversation_messages')
          .select('*', { count: 'exact', head: true })
          .eq('conversation_id', conv.id)
          .is('read_at', null)

        if (!error) {
          counts[conv.id] = count || 0
        }
      }

      return counts
    },
    enabled: !!conversationsData && conversationsData.length > 0,
  })

  const conversations = conversationsData || []
  const unreadCounts = unreadCountsData || {}

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }

  return (
    <div className="grid md:grid-cols-3 gap-6 h-[600px]">
      <div className={`md:col-span-1 card overflow-hidden ${selectedConversation ? 'hidden md:block' : ''}`}>
        <div className="px-4 py-3 bg-secondary-50 border-b border-secondary-200">
          <h3 className="font-semibold text-secondary-900">Conversations</h3>
          <p className="text-xs text-secondary-600">
            {conversations.length} total
          </p>
        </div>
        <div className="overflow-y-auto h-[calc(600px-60px)]">
          <ConversationList
            conversations={conversations}
            selectedId={selectedConversation?.id}
            onSelect={setSelectedConversation}
            unreadCounts={unreadCounts}
          />
        </div>
      </div>

      <div className={`md:col-span-2 card overflow-hidden ${!selectedConversation ? 'hidden md:block' : ''}`}>
        {selectedConversation ? (
          <ConversationView
            conversation={selectedConversation}
            onBack={() => setSelectedConversation(null)}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-secondary-300 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="text-secondary-600">Select a conversation to view messages</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
